/**
 * Pure PCM helpers for the whisper STT engine: downsampling to 16kHz mono and
 * the energy-based endpointer (VAD) that reproduces the legacy silence-window
 * commit contract. No DOM, no worker — fully unit-testable.
 */

export const WHISPER_SAMPLE_RATE = 16000;

/**
 * Downsample mono Float32 PCM from `fromRate` to 16kHz by averaging each
 * source bucket (cheap anti-aliased decimation — fine for speech). Returns
 * the input untouched when already 16kHz.
 */
export function downsampleTo16k(input: Float32Array, fromRate: number): Float32Array {
  if (fromRate === WHISPER_SAMPLE_RATE) return input;
  if (fromRate < WHISPER_SAMPLE_RATE) {
    // Upsampling speech buys nothing — repeat-sample to keep timings honest.
    const ratio = WHISPER_SAMPLE_RATE / fromRate;
    const out = new Float32Array(Math.floor(input.length * ratio));
    for (let i = 0; i < out.length; i++) {
      out[i] = input[Math.min(input.length - 1, Math.floor(i / ratio))];
    }
    return out;
  }
  const ratio = fromRate / WHISPER_SAMPLE_RATE;
  const outLen = Math.floor(input.length / ratio);
  const out = new Float32Array(outLen);
  for (let i = 0; i < outLen; i++) {
    const start = Math.floor(i * ratio);
    const end = Math.min(input.length, Math.floor((i + 1) * ratio));
    let sum = 0;
    for (let j = start; j < end; j++) sum += input[j];
    out[i] = end > start ? sum / (end - start) : 0;
  }
  return out;
}

/** Root-mean-square of a PCM frame — the energy measure the VAD keys on. */
export function rms(frame: Float32Array): number {
  if (frame.length === 0) return 0;
  let sum = 0;
  for (let i = 0; i < frame.length; i++) sum += frame[i] * frame[i];
  return Math.sqrt(sum / frame.length);
}

/**
 * Normalize an utterance's gain toward a target RMS before transcription —
 * whisper transcribes quiet audio markedly worse, and quiet mics are common.
 * Gain is capped so a near-silence blip can't be amplified into noise soup.
 */
export function normalizeUtterance(pcm: Float32Array, targetRms = 0.08, maxGain = 12): Float32Array {
  const level = rms(pcm);
  if (level <= 0) return pcm;
  const gain = Math.min(maxGain, targetRms / level);
  if (gain <= 1.05) return pcm; // already loud enough — don't touch
  const out = new Float32Array(pcm.length);
  for (let i = 0; i < pcm.length; i++) {
    const v = pcm[i] * gain;
    out[i] = v > 1 ? 1 : v < -1 ? -1 : v;
  }
  return out;
}

export type EndpointerEvent =
  | { type: "speech_started" }
  | { type: "commit"; pcm: Float32Array }
  | { type: "silent_window" }
  /** An utterance died at the junk gate — surfaced so drops are NEVER
   *  invisible in the field (bench 2026-07-23: a spoken "yes" vanished
   *  with nothing in the logs). */
  | { type: "blip"; speechMs: number; threshold: number };

export type EndpointerOpts = {
  /** Silence (ms) after speech before committing — the legacy silenceMs. */
  silenceMs: number;
  /** Hard cap on a single utterance (ms); commit at cap even mid-speech. */
  maxUtteranceMs?: number;
  /** MINIMUM speech threshold (RMS). The effective threshold is adaptive:
   *  max(speechRms, noiseFloor × 3) — a fixed value misjudged both quiet
   *  mics and noisy rooms (bench: transcribed ~10% of utterances). */
  speechRms?: number;
  /** Cumulative speech (ms) an utterance needs to be worth committing —
   *  keyboard clicks / breaths never reach the transcriber. */
  minSpeechMs?: number;
  sampleRate?: number;
};

export type Endpointer = {
  /** Feed a capture frame; returns events triggered by this frame. */
  push: (frame: Float32Array) => EndpointerEvent[];
  /** Flush: commit whatever is buffered if it contains speech. */
  flush: () => EndpointerEvent[];
  reset: () => void;
};

/**
 * Energy endpointer reproducing the legacy SpeechRecognition silence-window
 * behavior: speech resets the silence clock; `silenceMs` of quiet after speech
 * commits the utterance (with a little pre-roll retained so soft onsets
 * survive); a silence window with NO speech emits `silent_window` (the
 * onInterim("") contract). Deliberately simple — tune via opts, replace with a
 * model-based VAD later if field data demands it.
 */
export function createEndpointer(opts: EndpointerOpts): Endpointer {
  const sampleRate = opts.sampleRate ?? WHISPER_SAMPLE_RATE;
  // Low default: quiet mics (bench: normal speech under 0.006 RMS — had to
  // YELL). The adaptive floor guards noisy rooms; the junk gate guards blips.
  const minSpeechRms = opts.speechRms ?? 0.0035;
  // 160ms: a crisp "yes" is ~200-300ms of speech, and frames measured
  // against a TTS-bleed-raised threshold undercount it — 240ms ate real
  // answers (bench 2026-07-23). Clicks/breaths still die under 160ms.
  // Also covers short chip answers ("both", "paid") that 240ms dropped.
  const minSpeechMs = opts.minSpeechMs ?? 160;
  const maxMs = opts.maxUtteranceMs ?? 12000;
  const preRollMs = 240;

  let buffered: Float32Array[] = [];
  let bufferedMs = 0;
  let speechSeen = false;
  let speechMs = 0;
  let msSinceSpeech = 0;
  let msSinceStart = 0;
  /** EMA of non-speech RMS — the room's noise floor. */
  let noiseFloor = 0.004;

  const frameMs = (f: Float32Array) => (f.length / sampleRate) * 1000;
  const threshold = () => Math.max(minSpeechRms, noiseFloor * 2.5);

  const takeUtterance = (): Float32Array => {
    const total = buffered.reduce((a, f) => a + f.length, 0);
    const pcm = new Float32Array(total);
    let off = 0;
    for (const f of buffered) {
      pcm.set(f, off);
      off += f.length;
    }
    buffered = [];
    bufferedMs = 0;
    return pcm;
  };

  const reset = () => {
    buffered = [];
    bufferedMs = 0;
    speechSeen = false;
    speechMs = 0;
    msSinceSpeech = 0;
    msSinceStart = 0;
  };

  return {
    reset,
    push(frame) {
      const events: EndpointerEvent[] = [];
      const ms = frameMs(frame);
      msSinceStart += ms;
      buffered.push(frame);
      bufferedMs += ms;

      const level = rms(frame);
      // Min-tracking floor with capped rise (~1%/frame): real speech has
      // inter-word dips that keep pulling the floor DOWN, while sustained
      // room noise (no dips) raises it within a few seconds — updated on
      // EVERY frame, so noise louder than the initial threshold still gets
      // learned (an on-silence-only EMA never could).
      noiseFloor = Math.max(0.002, Math.min(level, noiseFloor * 1.01));
      const isSpeech = level >= threshold();
      if (isSpeech) {
        if (!speechSeen) {
          speechSeen = true;
          events.push({ type: "speech_started" });
        }
        speechMs += ms;
        msSinceSpeech = 0;
      } else {
        msSinceSpeech += ms;
      }

      if (!speechSeen) {
        // Keep only pre-roll while waiting for speech (bound memory) and
        // surface the silent-window signal once per window.
        while (bufferedMs > preRollMs && buffered.length > 1) {
          bufferedMs -= frameMs(buffered[0]);
          buffered.shift();
        }
        if (msSinceStart >= opts.silenceMs && msSinceSpeech >= opts.silenceMs) {
          events.push({ type: "silent_window" });
          msSinceSpeech = 0; // re-arm; emits again after another full window
          msSinceStart = 0;
        }
        return events;
      }

      if (msSinceSpeech >= opts.silenceMs || bufferedMs >= maxMs) {
        if (speechMs >= minSpeechMs) {
          events.push({ type: "commit", pcm: takeUtterance() });
        } else {
          // A blip (click, breath) — not worth a transcription round-trip;
          // drop the buffer and keep the window armed. Surface the drop.
          buffered = [];
          bufferedMs = 0;
          events.push({ type: "blip", speechMs, threshold: threshold() });
          events.push({ type: "silent_window" });
        }
        speechSeen = false;
        speechMs = 0;
        msSinceSpeech = 0;
        msSinceStart = 0;
      }
      return events;
    },
    flush() {
      // Explicit flush = the user (or the timeout-flush) ASKED for whatever
      // was said — no junk gate here. Gating flushes at minSpeechMs silently
      // ate short answers ("yes") on the stop/grace path (bench 2026-07-23);
      // downstream transcription + garbage filters handle true junk.
      if (!speechSeen || bufferedMs === 0) {
        reset();
        return [];
      }
      const pcm = takeUtterance();
      reset();
      return [{ type: "commit", pcm }];
    },
  };
}
